import type { LinkedInSignalTrigger } from '@/types/signals';

export const linkedinActionTranslationKeys: Record<LinkedInSignalTrigger | string, string> = {
  followed_page: 'linkedinSignalItem.actions.followed_page',
  commented_post: 'linkedinSignalItem.actions.commented_post',
  reacted_post: 'linkedinSignalItem.actions.reacted_post',
  authored_post: 'linkedinSignalItem.actions.authored_post',
};

export const linkedinActionParts: Record<string, { verb: string; connector?: string }> = {
  followed_page: {
    verb: 'linkedinSignalItem.verbs.followed_page',
  },
  followed_competitor: {
    verb: 'linkedinSignalItem.verbs.followed_competitor',
  },
  commented_post: {
    verb: 'linkedinSignalItem.verbs.commented_post',
  },
  reacted_post: {
    verb: 'linkedinSignalItem.verbs.reacted_post',
  },
  authored_post: {
    verb: 'linkedinSignalItem.verbs.authored_post',
  },
};

export type LinkedInSignalDisplaySource = {
  trigger?: string;
  url?: string;
  text?: string;
  post_title?: string;
  company_name?: string;
};

const withProtocol = (url: string) => {
  const trimmed = url.trim();
  if (!trimmed) return '';
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

export const getLinkedInSignalDisplay = ({
  action,
  signal,
}: {
  action?: string;
  signal?: LinkedInSignalDisplaySource | null;
}) => {
  const url = signal?.url?.trim() || '';
  const postTitle = signal?.post_title?.trim() || '';
  const companyName = signal?.company_name?.trim() || '';
  const normalizedAction = (action || signal?.trigger || '').toLowerCase().trim();

  let text = url;
  if (
    normalizedAction.includes('comment') ||
    normalizedAction.includes('react') ||
    normalizedAction.includes('author')
  ) {
    text = postTitle || url;
  } else if (normalizedAction.includes('follow')) {
    text = companyName || url;
  }

  const href = withProtocol(url);

  return { href, text };
};

export const reactionVerbMap: Record<string, { verb: string; connector?: string }> = {
  like: { verb: 'linkedinSignalItem.verbs.liked' },
  liked: { verb: 'linkedinSignalItem.verbs.liked' },
  celebrate: { verb: 'linkedinSignalItem.verbs.celebrated' },
  celebrated: { verb: 'linkedinSignalItem.verbs.celebrated' },
  support: { verb: 'linkedinSignalItem.verbs.supported' },
  supported: { verb: 'linkedinSignalItem.verbs.supported' },
  love: { verb: 'linkedinSignalItem.verbs.loved' },
  loved: { verb: 'linkedinSignalItem.verbs.loved' },
  insightful: { verb: 'linkedinSignalItem.verbs.insightful' },
  funny: { verb: 'linkedinSignalItem.verbs.funny' },
  laugh: { verb: 'linkedinSignalItem.verbs.funny' },
  laughing: { verb: 'linkedinSignalItem.verbs.funny' },
};

export const getLinkedinActionParts = (
  action?: string,
  reactionType?: string
): { verb: string; connector?: string } | undefined => {
  if (action === 'reacted_post' && reactionType) {
    const normalizedReaction = reactionType.toLowerCase().trim();
    if (reactionVerbMap[normalizedReaction]) {
      return reactionVerbMap[normalizedReaction];
    }
  }

  return action ? linkedinActionParts[action] : undefined;
};

export const getLinkedInSignalEmoji = (action?: string, reactionType?: string) => {
  if (action !== 'reacted_post') return null;

  switch (reactionType?.toLowerCase().trim()) {
    case 'celebrate':
    case 'celebrated':
      return '👏';
    case 'support':
    case 'supported':
      return '🫶';
    case 'love':
    case 'loved':
      return '❤️';
    case 'insightful':
      return '💡';
    case 'funny':
    case 'laugh':
    case 'laughing':
      return '😂';
    case 'like':
    case 'liked':
    default:
      return '👍';
  }
};
